/**
 * Détail d'un appel IA (`GET /traces/:call_id`) : l'appel, son payload de
 * diagnostic (prompts, sortie brute, corrections des gardes) s'il existe
 * encore (rétention 90 j), et ses signalements.
 *
 * Tiroir responsive (m-11) : pleine largeur sous 768 px. Les prompts restent
 * en LTR même en arabe (MON-15-B) : ce sont des textes techniques.
 */
import React from 'react';
import { Alert, Collapse, Descriptions, Drawer, Grid, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TraceDetailResponse } from '../../../api/monitoring/v2';
import { INK, STATE_COLORS } from '../monitoringTheme';
import QueryStatusView from '../shared/QueryStatusView';
import { useMonitoringQuery } from '../shared/useMonitoringQuery';
import { useMonitoringApiRef } from '../shared/useScopes';
import { useEnumLabel } from '../shared/i18nCatalog';
import { useFormat } from '../shared/format';
import { STATUS_COLOR } from '../shared/traceStatus';
import { directionOf } from '../../../i18n/uiLocale';

const { Text, Paragraph } = Typography;

const promptStyle: React.CSSProperties = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 420,
    overflow: 'auto',
    background: '#F8FAFC',
    borderRadius: 8,
    padding: 12,
    margin: 0,
    textAlign: 'left',
};

const PromptBlock: React.FC<{ text: string | null | undefined }> = ({ text }) => (
    <Paragraph copyable={text ? { text } : false} style={{ marginBottom: 0 }}>
        <pre dir="ltr" style={promptStyle}>
            {text || '—'}
        </pre>
    </Paragraph>
);

const TraceDrawer: React.FC<{ callId: string | null; onClose: () => void }> = ({
    callId,
    onClose,
}) => {
    const { t, i18n } = useTranslation();
    const f = useFormat();
    const enumLabel = useEnumLabel();
    const screens = Grid.useBreakpoint();
    const apiRef = useMonitoringApiRef();

    const query = useMonitoringQuery<TraceDetailResponse>(
        `trace:${callId ?? ''}`,
        (signal) => apiRef.current.getTrace(callId as string, signal),
        { enabled: callId !== null }
    );
    const detail = callId !== null ? query.data : null;
    const payload = detail?.payload ?? null;

    return (
        <Drawer
            title={
                <Space size={8} wrap>
                    {t('monitoring.traces.detail.title')}
                    {detail && (
                        <Tag color={STATUS_COLOR[detail.status] ?? STATE_COLORS.neutral} style={{ borderRadius: 10 }}>
                            {enumLabel('ai_status', detail.status)}
                        </Tag>
                    )}
                </Space>
            }
            placement={directionOf(i18n.language) === 'rtl' ? 'left' : 'right'}
            width={screens.md ? 840 : '100%'}
            open={callId !== null}
            onClose={onClose}
            styles={{ body: { paddingTop: 12 } }}
        >
            {callId !== null && <QueryStatusView query={query} tiles={0} />}
            {detail && (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions
                        size="small"
                        column={{ xs: 1, sm: 2 }}
                        items={[
                            { key: 'id', label: 'call_id', children: <Text copyable style={{ fontSize: 12 }}>{detail.call_id}</Text> },
                            { key: 'when', label: t('monitoring.traces.col.when'), children: f.dateTime(detail.started_at) },
                            {
                                key: 'learner',
                                label: t('monitoring.traces.col.learner'),
                                children: detail.learner?.name
                                    ? `${detail.learner.name}${detail.learner.id ? ` (#${detail.learner.id})` : ''}`
                                    : t('monitoring.traces.noLearner'),
                            },
                            {
                                key: 'op',
                                label: t('monitoring.traces.col.operation'),
                                children: `${enumLabel('ai_source', detail.source)} · ${enumLabel('ai_operation', detail.operation)}`,
                            },
                            {
                                key: 'model',
                                label: t('monitoring.col.model'),
                                children: `${detail.model_requested}${
                                    detail.model_served && detail.model_served !== detail.model_requested ? ` → ${detail.model_served}` : ''
                                }`,
                            },
                            {
                                key: 'outcome',
                                label: t('monitoring.traces.col.outcome'),
                                children: enumLabel('ai_outcome', detail.outcome),
                            },
                            { key: 'duration', label: t('monitoring.traces.col.duration'), children: f.durationMs(detail.duration_ms) },
                            {
                                key: 'tokens',
                                label: t('monitoring.traces.col.tokens'),
                                children: detail.tokens ? (
                                    <span dir="ltr">
                                        {(['input', 'cached', 'output', 'reasoning'] as const)
                                            .map((k) => `${t(`monitoring.tokens.${k}`)} ${f.compact(detail.tokens?.[k]).text}`)
                                            .join(' · ')}
                                    </span>
                                ) : (
                                    '—'
                                ),
                            },
                            { key: 'quest', label: 'quest_id', children: detail.quest_id ?? '—' },
                            { key: 'template', label: t('monitoring.traces.detail.template'), children: payload?.template_id ?? '—' },
                            { key: 'intent', label: t('monitoring.traces.detail.intent'), children: payload?.intent ?? '—' },
                        ]}
                    />

                    {detail.error && (
                        <Alert type="error" showIcon message={t('monitoring.traces.detail.error')} description={detail.error} />
                    )}

                    {detail.reports.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message={t('monitoring.traces.detail.reports')}
                            description={
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    {detail.reports.map((report) => (
                                        <div key={report.id} data-report-id={report.id}>
                                            <Space size={6} wrap>
                                                <Tag bordered={false}>{enumLabel('report_reason', report.reason)}</Tag>
                                                <Text style={{ fontSize: 12 }}>
                                                    {/* Auteur hors de la branche de l'observateur : masqué (§10.7). */}
                                                    {report.reporter
                                                        ? report.reporter.name ?? t('monitoring.traces.learnerHidden')
                                                        : t('monitoring.traces.detail.reporterHidden')}
                                                    {report.reporter_type ? ` (${report.reporter_type})` : ''}
                                                </Text>
                                                <Text style={{ fontSize: 11, color: INK.muted }}>
                                                    {f.dateTime(report.created_at)}
                                                </Text>
                                                <Tag bordered={false}>
                                                    {report.resolution
                                                        ? t('monitoring.traces.detail.resolution', { resolution: report.resolution })
                                                        : t('monitoring.traces.detail.unresolved')}
                                                </Tag>
                                            </Space>
                                            {report.comment && (
                                                <Paragraph style={{ fontSize: 12, margin: '4px 0 0', color: INK.secondary }}>
                                                    « {report.comment} »
                                                </Paragraph>
                                            )}
                                        </div>
                                    ))}
                                </Space>
                            }
                        />
                    )}

                    {payload === null ? (
                        <Alert type="info" showIcon message={t('monitoring.traces.detail.noPayload')} />
                    ) : (
                        <>
                            {payload.guard_events.length > 0 && (
                                <div>
                                    <Text strong style={{ fontSize: 13 }}>
                                        {t('monitoring.traces.detail.guardEvents')}
                                    </Text>
                                    <pre dir="ltr" style={{ ...promptStyle, marginTop: 8 }}>
                                        {JSON.stringify(payload.guard_events, null, 2)}
                                    </pre>
                                </div>
                            )}
                            <Collapse
                                items={[
                                    {
                                        key: 'system',
                                        label: t('monitoring.traces.detail.systemPrompt'),
                                        children: <PromptBlock text={payload.system_prompt} />,
                                    },
                                    {
                                        key: 'user',
                                        label: t('monitoring.traces.detail.userPrompt'),
                                        children: <PromptBlock text={payload.user_prompt} />,
                                    },
                                    {
                                        key: 'raw',
                                        label: t('monitoring.traces.detail.rawOutput'),
                                        children: <PromptBlock text={payload.raw_output} />,
                                    },
                                ]}
                            />
                        </>
                    )}

                    <div data-testid="trace-quest">
                        <Text strong style={{ fontSize: 13 }}>
                            {t('monitoring.traces.detail.quest')}
                        </Text>
                        {detail.quest === null ? (
                            <Paragraph style={{ fontSize: 12, color: INK.muted, marginTop: 8 }}>
                                {t('monitoring.traces.detail.noQuest')}
                            </Paragraph>
                        ) : (
                            <Collapse
                                style={{ marginTop: 8 }}
                                items={[
                                    {
                                        key: 'snapshot',
                                        label: t('monitoring.traces.detail.questSnapshot'),
                                        children: <PromptBlock text={JSON.stringify(detail.quest.snapshot, null, 2)} />,
                                    },
                                    {
                                        key: 'responses',
                                        label: t('monitoring.traces.detail.questResponses', { n: f.count(detail.quest.responses.length) }),
                                        children: <PromptBlock text={JSON.stringify(detail.quest.responses, null, 2)} />,
                                    },
                                ]}
                            />
                        )}
                    </div>
                </Space>
            )}
        </Drawer>
    );
};

export default TraceDrawer;
